import { useEffect, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Button, Card, SecondaryButton } from "@contractor-quoting/shared-ui";
import {
  minorFromMoney,
  moneyFromMinor,
  type SharedPricingResponse,
  type SharedRate,
} from "./quoting-setup-model.js";

interface Props {
  current: NonNullable<SharedPricingResponse["current"]>;
  editable: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}

const trades = [
  "painting",
  "flooring",
  "drywall",
  "tiling",
  "removal",
  "deck",
  "fence",
  "plumbing",
  "electrical",
  "windows",
  "doors",
  "trim",
];
const categories: SharedRate["category"][] = [
  "material",
  "labour",
  "removal",
  "disposal",
  "travel",
  "consumables",
  "fee",
];

export function SharedRateEditor(props: Props) {
  const [search, setSearch] = useState("");
  const filtered = props.current.rates.filter((rate) =>
    `${rate.tradeCode} ${rate.code} ${rate.description}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  return (
    <div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black">Rates</h2>
          <p className="text-sm text-stone-600">
            Rate codes stay stable because runbooks reference them.
          </p>
        </div>
        {props.editable ? <AddRate {...props} /> : null}
      </div>
      <input
        aria-label="Search shared pricing"
        className="mt-4 w-full rounded-xl border border-stone-300 bg-white px-4 py-3"
        placeholder="Search rates by trade, code, or description…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="mt-4 space-y-3">
        {filtered.map((rate) => (
          <RateRow {...props} key={rate.id} rate={rate} />
        ))}
        {!filtered.length ? (
          <Card>
            <p className="text-stone-600">No rates match this search.</p>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function RateRow({ current, rate, editable, onChanged, onError }: Props & { rate: SharedRate }) {
  const [draft, setDraft] = useState(rate);
  useEffect(() => setDraft(rate), [rate]);
  const save = async () => {
    try {
      await apiFetch(`/v1/contractor/shared-pricing/drafts/${current.versionId}/rates/${rate.id}`, {
        method: "PATCH",
        body: JSON.stringify({ expectedRevision: current.revision, rate: draft }),
      });
      onChanged();
    } catch (reason) {
      onError(errorMessage(reason));
    }
  };
  const remove = async () => {
    try {
      await apiFetch(`/v1/contractor/shared-pricing/drafts/${current.versionId}/rates/${rate.id}`, {
        method: "DELETE",
        body: JSON.stringify({ expectedRevision: current.revision }),
      });
      onChanged();
    } catch (reason) {
      onError(errorMessage(reason));
    }
  };
  const setConditionKey = (value: string) =>
    setDraft({
      ...draft,
      conditionKey: value || null,
      ...(!value ? { conditionOperator: null, conditionValue: null } : {}),
    });
  return (
    <Card>
      <div className="grid items-end gap-3 md:grid-cols-[9rem_1fr_9rem_auto]">
        <div>
          <p className="text-xs font-bold uppercase text-stone-500">{draft.tradeCode}</p>
          <p className="font-mono text-sm font-bold">{draft.code}</p>
        </div>
        <Setting label="Description">
          <input
            disabled={!editable}
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />
        </Setting>
        <Setting label={`Amount (${current.settings.currency})`}>
          <input
            disabled={!editable}
            type="number"
            min="0"
            step="0.01"
            value={moneyFromMinor(draft.amountMinor)}
            onChange={(event) =>
              setDraft({ ...draft, amountMinor: minorFromMoney(event.target.value) })
            }
          />
        </Setting>
        {editable ? (
          <SecondaryButton onClick={() => void save()}>Save rate</SecondaryButton>
        ) : (
          <Badge>{draft.priceBasis.replaceAll("_", " ")}</Badge>
        )}
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-sm font-bold text-emerald-800">
          Advanced rate settings
        </summary>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <SelectSetting
            label="Trade"
            disabled={!editable}
            value={draft.tradeCode}
            values={trades}
            onChange={(value) => setDraft({ ...draft, tradeCode: value })}
          />
          <SelectSetting
            label="Category"
            disabled={!editable}
            value={draft.category}
            values={categories}
            onChange={(value) => setDraft({ ...draft, category: value as SharedRate["category"] })}
          />
          <SelectSetting
            label="Unit"
            disabled={!editable}
            value={draft.unit}
            values={["square_foot", "linear_foot", "hour", "item", "fixed"]}
            onChange={(value) => setDraft({ ...draft, unit: value as SharedRate["unit"] })}
          />
          <Setting label="Quantity source">
            <input
              disabled={!editable}
              value={draft.quantitySource}
              onChange={(event) => setDraft({ ...draft, quantitySource: event.target.value })}
            />
          </Setting>
          <SelectSetting
            label="Price basis"
            disabled={!editable}
            value={draft.priceBasis}
            values={["sell_price", "cost", "fixed_package"]}
            onChange={(value) =>
              setDraft({
                ...draft,
                priceBasis: value as SharedRate["priceBasis"],
                markupApplies: value === "cost" && draft.markupApplies,
              })
            }
          />
          <Setting label="Tax class">
            <input
              disabled={!editable}
              value={draft.taxClass}
              onChange={(event) => setDraft({ ...draft, taxClass: event.target.value })}
            />
          </Setting>
          <Setting label="Condition field">
            <input
              disabled={!editable}
              value={draft.conditionKey ?? ""}
              onChange={(event) => setConditionKey(event.target.value)}
            />
          </Setting>
          <SelectSetting
            label="Condition operator"
            disabled={!editable || !draft.conditionKey}
            value={draft.conditionOperator ?? ""}
            values={["", "equals", "not_equals", "includes", "truthy"]}
            onChange={(value) =>
              setDraft({
                ...draft,
                conditionOperator: (value || null) as SharedRate["conditionOperator"],
              })
            }
          />
          <Setting label="Condition value">
            <input
              disabled={!editable || !draft.conditionKey}
              value={draft.conditionValue ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, conditionValue: event.target.value || null })
              }
            />
          </Setting>
          <PercentSetting
            label="Measurement allowance %"
            disabled={!editable}
            value={draft.measurementAllowanceBps}
            onChange={(value) => setDraft({ ...draft, measurementAllowanceBps: value })}
          />
          <PercentSetting
            label="Material waste %"
            disabled={!editable}
            value={draft.materialWasteBps}
            onChange={(value) => setDraft({ ...draft, materialWasteBps: value })}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-5">
          <Toggle
            checked={draft.taxable}
            disabled={!editable}
            label="Taxable"
            onChange={(value) => setDraft({ ...draft, taxable: value })}
          />
          <Toggle
            checked={draft.markupApplies}
            disabled={!editable || draft.priceBasis !== "cost"}
            label="Markup applies"
            onChange={(value) => setDraft({ ...draft, markupApplies: value })}
          />
          <Toggle
            checked={draft.contingencyApplies}
            disabled={!editable}
            label="Contingency applies"
            onChange={(value) => setDraft({ ...draft, contingencyApplies: value })}
          />
        </div>
        {editable ? (
          <button className="mt-3 text-sm font-bold text-red-700" onClick={() => void remove()}>
            Remove draft rate
          </button>
        ) : null}
      </details>
    </Card>
  );
}

function AddRate({ current, onChanged, onError }: Props) {
  const [open, setOpen] = useState(false);
  const [tradeCode, setTrade] = useState("painting");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const add = async () => {
    try {
      await apiFetch(`/v1/contractor/shared-pricing/drafts/${current.versionId}/rates`, {
        method: "POST",
        body: JSON.stringify({
          expectedRevision: current.revision,
          rate: {
            tradeCode,
            code,
            description,
            amountMinor: minorFromMoney(amount),
            category: "labour",
            unit: "fixed",
            quantitySource: "fixed:1",
            conditionKey: null,
            conditionOperator: null,
            conditionValue: null,
            priceBasis: "sell_price",
            taxClass: "standard",
            taxable: true,
            measurementAllowanceBps: 0,
            materialWasteBps: 0,
            markupApplies: false,
            contingencyApplies: true,
          },
        }),
      });
      setOpen(false);
      setCode("");
      setDescription("");
      setAmount("");
      onChanged();
    } catch (reason) {
      onError(errorMessage(reason));
    }
  };
  if (!open) return <Button onClick={() => setOpen(true)}>Add rate</Button>;
  return (
    <Card className="w-full">
      <div className="grid gap-3 md:grid-cols-4">
        <SelectSetting label="Trade" value={tradeCode} values={trades} onChange={setTrade} />
        <Setting label="Stable code">
          <input value={code} onChange={(event) => setCode(event.target.value)} />
        </Setting>
        <Setting label="Description">
          <input value={description} onChange={(event) => setDescription(event.target.value)} />
        </Setting>
        <Setting label="Amount">
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Setting>
      </div>
      <div className="mt-3 flex gap-2">
        <Button disabled={!code || !description || !amount} onClick={() => void add()}>
          Add to draft
        </Button>
        <SecondaryButton onClick={() => setOpen(false)}>Cancel</SecondaryButton>
      </div>
    </Card>
  );
}

function Setting({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <label className="block text-sm font-bold text-stone-700">
      <span className="mb-1 block">{label}</span>
      <span className="[&>input]:w-full [&>input]:rounded-lg [&>input]:border [&>input]:p-2">
        {children}
      </span>
    </label>
  );
}
function SelectSetting({
  label,
  value,
  values,
  disabled = false,
  onChange,
}: {
  label: string;
  value: string;
  values: readonly string[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Setting label={label}>
      <select
        className="w-full rounded-lg border p-2"
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {item || "None"}
          </option>
        ))}
      </select>
    </Setting>
  );
}
function PercentSetting({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <Setting label={label}>
      <input
        disabled={disabled}
        type="number"
        step="0.01"
        value={value / 100}
        onChange={(event) => onChange(Math.round(Number(event.target.value || 0) * 100))}
      />
    </Setting>
  );
}
function Toggle({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm font-bold text-stone-700">
      <input
        checked={checked}
        disabled={disabled}
        type="checkbox"
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}
function errorMessage(reason: unknown) {
  return reason instanceof Error ? reason.message : "Shared pricing could not be updated.";
}
